import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled, { theme, prop } from '@nobia/zeus-components/lib/styled'
import InstallationModal from './InstallationModal'
import { formatPrice, getInstallationPrice } from '../cards/utils'
import { useTranslation } from '../../utils/useTranslation'

const Button = styled.button`
  background-color: ${theme('colors.white')};
  min-width: 300px;
  width: 300px;
  border-radius: 6px;
  margin: 10px;
  padding: 0;
  scroll-snap-align: center;
  transition: opacity 150ms ease;
  border: 2px solid ${(p) => p.theme.card.border[p.color]};
  opacity: ${(p) => (p.color === 'selected' ? 1 : 0.75)};
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    opacity: 1;
  }
`

const Info = styled.div`
  text-align: center;
  padding: ${prop('theme.body.padding')};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Title = styled.h4`
  margin: 0;
`

const Description = styled.p`
  margin: 10px 0;
`

const Price = styled.strong`
  font-family: ${prop('theme.font.paragraph.fontFamily')};
  font-weight: 500;
  font-size: 16px;
  margin-top: auto;
  color: ${theme('colors.primary')};
`

const InstallationCard = ({
  card,
  selection,
  priceText,
  defmessage,
  selected,
  onSelect,
}) => {
  const { country } = useTranslation()
  const { t } = useTranslation('app.components.installation')
  const [showModal, setShowModal] = useState(false)
  const [storeBand, setStoreBand] = useState(selection?.installation?.storeBand || '')
  const [hasChargedFee, setHasChargedFee] = useState(selection?.installation?.hasChargedFee || false)
  const { title, description } = card || {}
  const displayPrice = priceText !== '0 kr' ? priceText : defmessage
  const color = selected ? 'selected' : 'default'
  const cardType = card?.cardType || 0
  const unitType = selection?.units?.type || ''
  const startingText = `${t('price-estimate')}: `

  const handleCardClick = () => {
    onSelect(card, true)

    if (cardType !== 0) {
      setShowModal(true)
    }
  }

  const updateCard = (cardData) => {
    if (!cardData?.storeBand) return

    const { storeBand, hasChargedFee, location, highRise, parkingDifficult } =
      cardData || {}

    const updatedCard = {
      ...card,
      id: 'installation',
      storeBand,
      hasChargedFee,
      location,
      highRise,
      parkingDifficult,
    }

    onSelect(updatedCard, false)
  }

  const handleModalClose = (cardData) => {
    updateCard(cardData)
    setShowModal(false)
  }

  const handleModalSaveAndClose = (cardData) => {
    const { storeBand, hasChargedFee } = cardData || {}

    updateCard(cardData)
    setStoreBand(storeBand)
    setHasChargedFee(hasChargedFee)
    setShowModal(false)
  }

  const formattedPrice = useMemo(() => {
    if (storeBand) {
      const price = getInstallationPrice(
        cardType,
        unitType,
        storeBand,
        hasChargedFee
      )

      return formatPrice({ price, country, startingText })
    }

    return displayPrice
  }, [
    cardType,
    unitType,
    storeBand,
    hasChargedFee,
    displayPrice,
    country,
    startingText,
  ])

  return (
    <>
      <Button color={color} onClick={handleCardClick}>
        <Info>
          <Title>{title}</Title>
          <Description>{description}</Description>
          <Price>{formattedPrice}</Price>
        </Info>
      </Button>

      <InstallationModal
        isOpen={showModal}
        selection={selection}
        onClose={handleModalClose}
        onSaveAndClose={handleModalSaveAndClose}
        title={title}
        priceText={priceText}
        defmessage={defmessage}
        cardType={cardType}
        unitType={unitType}
      />
    </>
  )
}

InstallationCard.defaultProps = {
  card: null,
  priceText: null,
  defmessage: null,
}

InstallationCard.propTypes = {
  card: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    cardType: PropTypes.number,
  }),
  selection: PropTypes.shape({}),
  priceText: PropTypes.string,
  defmessage: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
}

export default InstallationCard
